import type { Schedule } from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2, Calendar, Clock, Eye, Users } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchSchedulesByStaffMemberQueryOptions } from "@/api/schedules";
import { DataTable } from "@/components/data-table";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/staff/$staffId/schedules",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, staffId } = params;

		// Preload schedules data
		context.queryClient.ensureQueryData(
			fetchSchedulesByStaffMemberQueryOptions(userId, token, orgId, staffId),
		);

		return { userId, token, orgId, staffId };
	},
	component: StaffSchedulesPage,
});

function StaffSchedulesPage() {
	const { t } = useTranslation();
	const { orgId, staffId } = Route.useParams();
	const { userId, token } = Route.useLoaderData();
	const { formatDate } = useDateFormatter();

	// Fetch schedules data
	const { data: schedules } = useSuspenseQuery(
		fetchSchedulesByStaffMemberQueryOptions(userId, token, orgId, staffId),
	);

	const columns: ColumnDef<Schedule>[] = useMemo(
		() => [
			{
				accessorKey: "name",
				header: () => (
					<div className="flex items-center gap-2">
						<Calendar className="w-4 h-4" />
						{t("common.name")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div>
							<div className="font-medium">{schedule.name}</div>
							{schedule.description && (
								<div className="text-sm text-base-content/70">
									{schedule.description}
								</div>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "establishment",
				header: () => (
					<div className="flex items-center gap-2">
						<Building2 className="w-4 h-4" />
						{t("common.establishment")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							{schedule.establishment?.name || "-"}
						</div>
					);
				},
			},
			{
				accessorKey: "type",
				header: t("common.type"),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<span className="badge badge-outline">
								{t(`common.${schedule.type.toLowerCase()}`)}
							</span>
						</div>
					);
				},
			},
			{
				accessorKey: "time",
				header: () => (
					<div className="flex items-center gap-2">
						<Clock className="w-4 h-4" />
						{t("common.time")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<div className="font-medium">
								{schedule.startTime} - {schedule.endTime}
							</div>
							<div className="text-sm text-base-content/70">
								{schedule.daysOfWeek
									.map((day) => t(`common.${day.toLowerCase()}`))
									.join(", ")}
							</div>
						</div>
					);
				},
			},
			{
				accessorKey: "enrollmentCount",
				header: () => (
					<div className="flex items-center gap-2">
						<Users className="w-4 h-4" />
						{t("common.enrollments")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<span className="font-medium">
								{schedule.enrollmentCount || 0}
								{schedule.type === "GROUP" && schedule.capacity
									? ` / ${schedule.capacity}`
									: ""}
							</span>
						</div>
					);
				},
			},
			{
				accessorKey: "period",
				header: t("common.period"),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<div className="font-medium">
								{formatDate(new Date(schedule.startDate).getTime())}
							</div>
							{schedule.endDate && (
								<div className="text-sm text-base-content/70">
									{t("common.to")}{" "}
									{formatDate(new Date(schedule.endDate).getTime())}
								</div>
							)}
						</div>
					);
				},
			},
			{
				id: "actions",
				header: t("common.actions"),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="flex justify-center">
							<Link
								to="/app/org/$orgId/schedules/$scheduleId"
								params={{
									orgId,
									scheduleId: schedule.id,
								}}
								className="btn btn-ghost btn-sm"
								title={t("common.view")}
							>
								<Eye className="w-4 h-4" />
								{t("common.view")}
							</Link>
						</div>
					);
				},
			},
		],
		[t, formatDate, orgId],
	);

	return (
		<div className="h-full flex flex-col space-y-6">
			<div className="h-full overflow-auto">
				<DataTable
					data={schedules}
					isLoading={false}
					error={null}
					columns={columns}
					testId="staff-schedules-table"
					canManage={false}
					emptyState={{
						title: t("common.noSchedules"),
						description: t("common.noSchedulesDescription"),
					}}
				/>
			</div>
		</div>
	);
}
